import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import RichTextEditor from './RichTextEditor';
import { dateToUTCNoon } from '../utils/dateHelpers';

interface ProblemStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedCase: any) => void;
  caseId: string;
  currentState: string;
  targetState: string;
  actionLabel: string;
}

interface FormData {
  assessment1Summary: string;
  assessment2Summary: string;
  rootCause: string;
  workaround: string;
  resolution: string;
  solutionImplemented: boolean;
  implementationDate: string;
  cancellationReason: string;
}

const PROBLEM_STATE_LABELS = {
  new: 'New',
  assess_1: 'Assess 1',
  assess_2: 'Assess 2',
  root_cause_analysis: 'Root Cause Analysis',
  resolved: 'Resolved',
  closed: 'Closed',
  cancelled: 'Cancelled'
};

const ProblemStatusModal: React.FC<ProblemStatusModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  caseId,
  currentState,
  targetState,
  actionLabel
}) => {
  const [formData, setFormData] = useState<FormData>({
    assessment1Summary: '',
    assessment2Summary: '',
    rootCause: '',
    workaround: '',
    resolution: '',
    solutionImplemented: false,
    implementationDate: '',
    cancellationReason: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        assessment1Summary: '',
        assessment2Summary: '',
        rootCause: '',
        workaround: '',
        resolution: '',
        solutionImplemented: false,
        implementationDate: '',
        cancellationReason: ''
      });
      setError(null);
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getRequiredFields = (): (keyof FormData)[] => {
    switch (targetState) {
      case 'assess_1':
        return ['assessment1Summary'];
      case 'assess_2':
        return ['assessment2Summary'];
      case 'root_cause_analysis':
        return ['rootCause', 'workaround'];
      case 'resolved':
        return ['resolution'];
      case 'cancelled':
        return ['cancellationReason'];
      default:
        return [];
    }
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];
    const requiredFields = getRequiredFields();

    requiredFields.forEach(field => {
      if (field === 'solutionImplemented') return; // boolean field, always valid
      
      const value = formData[field];
      if (!value || (typeof value === 'string' && !value.trim())) {
        const fieldLabels = {
          assessment1Summary: 'Assessment Summary 1',
          assessment2Summary: 'Assessment Summary 2',
          rootCause: 'Root Cause',
          workaround: 'Workaround',
          resolution: 'Resolution',
          implementationDate: 'Implementation Date',
          cancellationReason: 'Cancellation Reason'
        };
        errors.push(`${fieldLabels[field as keyof typeof fieldLabels]} is required`);
      }
    });

    // Special validation for resolved state: solutionImplemented must be checked
    if (targetState === 'resolved' && !formData.solutionImplemented) {
      errors.push('Solution Implemented must be checked to move to Resolved state');
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare the data to send
      const requestData: any = {
        problemState: targetState
      };

      // Only include relevant fields based on target state
      const requiredFields = getRequiredFields();
      requiredFields.forEach(field => {
        requestData[field] = formData[field];
      });

      // For resolved state, always include solution implementation fields
      if (targetState === 'resolved') {
        requestData.solutionImplemented = formData.solutionImplemented;
        if (formData.solutionImplemented && formData.implementationDate) {
          requestData.implementationDate = dateToUTCNoon(formData.implementationDate);
        }
      }

      const response = await api.patch(`/cases/${caseId}/problem-status`, requestData);
      
      onSuccess(response.data);
      onClose();
    } catch (error: any) {
      console.error('Error updating problem status:', error);
      setError(error.response?.data?.message || 'Failed to update problem status');
    } finally {
      setLoading(false);
    }
  };

  const renderFormFields = () => {
    switch (targetState) {
      case 'assess_1':
        return (
          <div>
            <label htmlFor="assessment1Summary" className="block text-sm font-medium text-gray-700 mb-2">
              Assessment Summary 1 *
            </label>
            <RichTextEditor
              value={formData.assessment1Summary}
              onChange={(value) => handleInputChange('assessment1Summary', value)}
              placeholder="Enter assessment summary for first assessment..."
              minHeight="120px"
            />
          </div>
        );

      case 'assess_2':
        return (
          <div>
            <label htmlFor="assessment2Summary" className="block text-sm font-medium text-gray-700 mb-2">
              Assessment Summary 2 *
            </label>
            <RichTextEditor
              value={formData.assessment2Summary}
              onChange={(value) => handleInputChange('assessment2Summary', value)}
              placeholder="Enter assessment summary for second assessment..."
              minHeight="120px"
            />
          </div>
        );

      case 'root_cause_analysis':
        return (
          <div className="space-y-4">
            <div>
              <label htmlFor="rootCause" className="block text-sm font-medium text-gray-700 mb-2">
                Root Cause *
              </label>
              <RichTextEditor
                value={formData.rootCause}
                onChange={(value) => handleInputChange('rootCause', value)}
                placeholder="Describe the root cause of the problem..."
                minHeight="120px"
              />
            </div>
            <div>
              <label htmlFor="workaround" className="block text-sm font-medium text-gray-700 mb-2">
                Workaround *
              </label>
              <RichTextEditor
                value={formData.workaround}
                onChange={(value) => handleInputChange('workaround', value)}
                placeholder="Describe the workaround solution..."
                minHeight="120px"
              />
            </div>
          </div>
        );

      case 'resolved':
        return (
          <div className="space-y-4">
            <div>
              <label htmlFor="resolution" className="block text-sm font-medium text-gray-700 mb-2">
                Resolution *
              </label>
              <RichTextEditor
                value={formData.resolution}
                onChange={(value) => handleInputChange('resolution', value)}
                placeholder="Describe how the problem was resolved..."
                minHeight="120px"
              />
            </div>
            <div>
              <div className="flex items-center">
                <input
                  id="solutionImplemented"
                  type="checkbox"
                  checked={formData.solutionImplemented}
                  onChange={(e) => handleInputChange('solutionImplemented', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="solutionImplemented" className="ml-2 block text-sm text-gray-900">
                  Solution Implemented? <span className="text-red-500">*</span>
                </label>
              </div>
            </div>
          </div>
        );

      case 'cancelled':
        return (
          <div>
            <label htmlFor="cancellationReason" className="block text-sm font-medium text-gray-700 mb-2">
              Cancellation Reason *
            </label>
            <RichTextEditor
              value={formData.cancellationReason}
              onChange={(value) => handleInputChange('cancellationReason', value)}
              placeholder="Please provide a detailed reason for cancelling this problem..."
              minHeight="120px"
            />
          </div>
        );

      default:
        return null;
    }
  };

  const hasRequiredFields = getRequiredFields().length > 0;

  // Check if submit button should be disabled for resolved state
  const isResolvedButtonDisabled = () => {
    if (targetState === 'resolved') {
      return !formData.resolution.trim() || !formData.solutionImplemented;
    }
    return false;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-8 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 flex items-center justify-between"
                >
                  <span>{actionLabel}</span>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </Dialog.Title>
                
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Changing status from <strong>{PROBLEM_STATE_LABELS[currentState as keyof typeof PROBLEM_STATE_LABELS] || currentState}</strong> to <strong>{PROBLEM_STATE_LABELS[targetState as keyof typeof PROBLEM_STATE_LABELS] || targetState}</strong>
                  </p>
                </div>

                {error && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {hasRequiredFields && (
                  <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                    {renderFormFields()}

                    <div className="mt-6 flex justify-end space-x-3">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        onClick={onClose}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                        disabled={loading || isResolvedButtonDisabled()}
                      >
                        {loading ? 'Updating...' : actionLabel}
                      </button>
                    </div>
                  </form>
                )}

                {!hasRequiredFields && (
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={onClose}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                      disabled={loading}
                    >
                      {loading ? 'Updating...' : actionLabel}
                    </button>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ProblemStatusModal;